import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Eye, ShoppingCart, Star, Ban } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from '../hooks/use-toast';
import QuickViewModal from './QuickViewModal';
import { primeCache, cachedGet } from '../lib/apiCache';

const ProductCard = ({ product, showProgress = false }) => {
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useCart();
  const { language, t } = useLanguage();
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const inWishlist = isInWishlist(product.id);

  const productName =
    language === 'ru' && product.nameRu ? product.nameRu : product.name;

  const productSlug =
    language === 'ru' && product.slugRu
      ? product.slugRu
      : product.slug || product.id;

  const productBadge =
    language === 'ru' && product.badgeRu ? product.badgeRu : product.badge;

  const productStoreName =
    language === 'ru' && product.storeNameRu
      ? product.storeNameRu
      : product.storeName;

  const hasDiscount =
    product.originalPrice &&
    Number(product.originalPrice) > Number(product.price);

  const discountPercent = hasDiscount
    ? Math.round(
        ((Number(product.originalPrice) - Number(product.price)) /
          Number(product.originalPrice)) *
          100
      )
    : 0;

  const isOutOfStock =
    product.inStock === false ||
    Number(product.available ?? 0) <= 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isOutOfStock) {
      toast({
        title: language === 'ru' ? 'Нет в наличии' : 'Stoc epuizat',
        description:
          language === 'ru'
            ? 'Этот товар временно отсутствует.'
            : 'Acest produs este momentan indisponibil.',
        variant: 'destructive',
      });
      return;
    }

    addToCart({
      ...product,
      quantity: 1,
      selectedSize: product.sizes?.[0] || null,
      selectedColor: product.colors?.[0] || null,
    });

    toast({
      title: t('productCard.success'),
      description: `${productName} ${t('productCard.addedToCart')}`,
    });
  };

  const handleAddToWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (inWishlist) {
      removeFromWishlist(product.id);

      toast({
        title: t('productCard.success'),
        description: `${productName} ${
          t('productCard.removedFromWishlist') || 'eliminat din favorite'
        }`,
      });
    } else {
      addToWishlist(product);

      toast({
        title: t('productCard.success'),
        description: `${productName} ${t('productCard.addedToWishlist')}`,
      });
    }
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  return (
    <>
      <Link
        to={`/product/${productSlug}`}
        state={{ product }}
        onMouseEnter={() => {
          // Prime the product cache & warm related endpoints so the detail page
          // renders instantly when the user clicks.
          primeCache(`/api/products/${productSlug}`, product);
          cachedGet(`/api/products/${productSlug}`).catch(() => {});
        }}
        className="block h-full"
      >
  <div
    className={`bg-white rounded-[20px] md:rounded-[24px] overflow-hidden border transition group relative h-full flex flex-col ${
      hasDiscount ? 'border-red-500' : 'border-[#e2e2e2]'
    } ${isOutOfStock ? 'opacity-70 grayscale-[35%]' : ''}`}
    data-testid={isOutOfStock ? 'product-card-oos' : 'product-card'}
  >
{/* Out-of-stock badge */}
{isOutOfStock && (
  <div
    className="absolute right-3 top-3 md:right-4 md:top-4 z-20 pointer-events-none"
    data-testid="oos-badge"
  >
    <span className="inline-flex items-center gap-2 rounded-full bg-[#c8ff62] px-4 py-2 text-[13px] font-semibold leading-none text-[#17324d] shadow-sm md:px-5 md:py-2.5 md:text-[15px]">
      <Ban className="h-4 w-4 md:h-5 md:w-5" strokeWidth={2.4} />
      {language === 'ru' ? 'Нет в наличии' : 'Stoc epuizat'}
    </span>
  </div>
)}

    {/* Badges */}
    {(productBadge || hasDiscount) && (
      <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10 flex flex-col items-start gap-1.5">
        {productBadge && (
          <span className="bg-[#a7cf26] text-white text-[10px] md:text-xs font-semibold px-2.5 md:px-3 py-1 rounded-full">
            {productBadge}
          </span>
        )}

        {hasDiscount && (
          <span className="bg-red-500 text-white text-[10px] md:text-xs font-extrabold px-2.5 md:px-3 py-1 rounded-full">
            -{discountPercent}%
          </span>
        )}
      </div>
    )}

          {/* Product Image */}
          <div className="relative bg-white aspect-square overflow-hidden flex items-center justify-center p-3">
  <img
    src={product.image}
    alt={product.name}
    loading="lazy"
    decoding="async"
    className="max-w-full max-h-full object-contain object-center"
  />
</div>

          {/* Product Info */}
          <div className="px-3.5 pb-3 pt-2.5 md:px-5 md:pb-2 md:pt-4 flex flex-col flex-1">
            {productStoreName && (
              <p className="text-[12px] md:text-[14px] leading-none text-[#b5b5b5] font-normal mb-2 md:mb-3 truncate">
                {productStoreName}
              </p>
            )}

            <h3 className="h-[38px] md:h-[43px] text-[14px] md:text-[16px] leading-[1.35] font-semibold text-[#333333] line-clamp-2 overflow-hidden">
  {productName}
</h3>
          </div>

          {/* Bottom Price Row */}
          <div className="border-t border-[#e2e2e2] px-3.5 py-3 md:px-5 md:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 md:gap-2 flex-wrap min-w-0">
              {hasDiscount && (
                <span className="text-[12px] md:text-[14px] text-gray-400 line-through leading-none">
                  {product.originalPrice} MDL
                </span>
              )}

              <span
                className={`text-[14px] md:text-[16px] leading-none font-medium ${
                  hasDiscount ? 'text-red-500' : 'text-[#9dcc24]'
                }`}
              >
                {product.price} MDL
              </span>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={isOutOfStock}
              className={`shrink-0 w-8 h-8 md:w-9 md:h-9 flex items-center justify-center transition ${
                isOutOfStock
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-[#333333] hover:text-[#9dcc24]'
              }`}
              aria-label={
                isOutOfStock
                  ? language === 'ru'
                    ? 'Нет в наличии'
                    : 'Stoc epuizat'
                  : t('productCard.buy') || 'Adaugă în coș'
              }
              data-testid="product-card-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </Link>

      <QuickViewModal
        isOpen={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductCard;